import { Globe } from 'lucide-react';
import { hostingPlansBySlug, type HostingPlan } from '@sighthost/config';

/**
 * PlanRecapCard — mirror of the customer's in-flight order on AuthLayout's
 * left panel. Reads sessionStorage['pending_hosting_order_v1'] (same key
 * NewServicePage uses, see portal/src/apps/client/pages/services/NewServicePage.tsx:85)
 * and resolves the plan against the build-time hostingPlans catalog.
 *
 * Why we read from sessionStorage (and not refetch from the DB):
 *   - The wizard is the SOLE writer of this key.
 *   - The DB lookup would require an authenticated session — defeats the
 *     point of showing context to a yet-to-sign-up guest.
 *   - hostingPlans is the canonical marketing-display source (see
 *     packages/config/src/hosting-plans.ts header).
 *
 * Spec: _bmad-output/AUTH_AND_TRUST_BRD_2026-05-17.md FR-9.
 */

const SESSION_KEY = 'pending_hosting_order_v1';
const SESSION_TTL_MS = 30 * 60_000;

export interface WizardPlanState {
  /** Plan slug as stored in sessionStorage (must match service_plan.slug). */
  slug: string;
  /** Plan name resolved from hostingPlansBySlug. */
  name: string;
  /** Monthly price in ZAR. */
  priceZar: number;
  /** Plan-card features (first 3 bullets — display only). */
  features: string[];
  /** Domain mode the customer picked at Step 2 (if any). */
  fqdnMode: 'register' | 'existing' | 'later' | null;
  /** Domain string they entered (if any). */
  fqdn: string | null;
}

interface RawPendingOrder {
  step: 1 | 2 | 3;
  planSlug: string | null;
  fqdnMode: 'register' | 'existing' | 'later' | null;
  registerFqdn: string;
  existingFqdn: string;
  savedAt: number;
}

export function loadWizardPlan(): WizardPlanState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RawPendingOrder;
    if (Date.now() - parsed.savedAt > SESSION_TTL_MS) return null;
    if (!parsed.planSlug) return null;
    const plan: HostingPlan | undefined = hostingPlansBySlug[parsed.planSlug];
    if (!plan) return null;
    const fqdn =
      parsed.fqdnMode === 'register'
        ? parsed.registerFqdn || null
        : parsed.fqdnMode === 'existing'
          ? parsed.existingFqdn || null
          : null;
    return {
      slug: plan.slug,
      name: plan.name,
      priceZar: plan.priceZar,
      features: plan.features.slice(0, 3),
      fqdnMode: parsed.fqdnMode,
      fqdn,
    };
  } catch {
    return null;
  }
}

interface PlanRecapCardProps {
  plan: WizardPlanState;
}

export function PlanRecapCard({ plan }: PlanRecapCardProps) {
  return (
    <div className="rounded-md border border-border bg-card p-5 shadow-card">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="font-display text-lg font-bold tracking-tight text-ink">
          {plan.name}
        </span>
        <span className="font-display text-2xl font-bold tabular-nums text-ink">
          R {plan.priceZar}
          <span className="ml-1 font-mono text-xs font-normal uppercase tracking-wider text-muted-foreground">
            / mo
          </span>
        </span>
      </div>

      {plan.fqdn && plan.fqdnMode === 'register' && (
        <div className="mb-4 flex items-center gap-2 rounded-sm bg-primary/5 px-3 py-2 text-xs text-ink">
          <Globe className="size-3.5 text-primary" aria-hidden />
          <span>
            Registering <strong>{plan.fqdn}</strong> with this order
          </span>
        </div>
      )}
      {plan.fqdn && plan.fqdnMode === 'existing' && (
        <div className="mb-4 flex items-center gap-2 rounded-sm bg-secondary/5 px-3 py-2 text-xs text-ink">
          <Globe className="size-3.5 text-secondary-ink" aria-hidden />
          <span>
            Using your existing <strong>{plan.fqdn}</strong>
          </span>
        </div>
      )}
      {plan.fqdnMode === 'later' && (
        <div className="mb-4 rounded-sm bg-muted px-3 py-2 text-xs text-muted-foreground">
          Setting up on a temporary URL — attach a domain anytime.
        </div>
      )}

      <ul className="space-y-1.5 text-xs text-ink-soft">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-1.5">
            <span className="mt-1 size-1 shrink-0 rounded-full bg-primary" aria-hidden />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
